import {
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import EntityModalButton from "../../components/EntityModalButton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import {
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../components/EntityProfileFeedback";
import useEntityProfileToast from "../../hooks/useEntityProfileToast";
import RelationshipForm from "./components/RelationshipForm";
import RelationshipTable from "./components/RelationshipTable";
import type {
  PatientCaregiverRelationship,
  RelationshipDraft,
  RelationshipOwner,
  RelationshipWithDisplayData,
} from "./caregiverRelationships.types";
import {
  createDraft,
  getLookupOption,
  getRelatedEntityId,
  mapRelationshipToDraft,
  normalizeRelationshipTypes,
} from "./caregiverRelationships.utils";

type Props = {
  onOpenRelatedEntity: (relationship: RelationshipWithDisplayData) => void;
  owner: RelationshipOwner;
};

export type { RelationshipOwner };

export default function EntityProfileCaregiverRelationshipsSection(props: Props) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const [draft, setDraft] = React.useState<RelationshipDraft | null>(null);
  const relatedEntity = props.owner.type === "Patient" ? "caregiver" : "patient";
  const ownerEntity = props.owner.type === "Patient" ? "patient" : "caregiver";
  const relationshipsQueryKey = [
    "entityProfileModal",
    "caregiverRelationships",
    props.owner.type,
    props.owner.id,
  ];
  const relationshipsQuery = useQuery({
    queryKey: relationshipsQueryKey,
    queryFn: () =>
      api.get("./patient_caregiver_relationship", {
        query:
          props.owner.type === "Patient"
            ? { patientId: props.owner.id }
            : { caregiverId: props.owner.id },
      }),
  });
  const relationshipTypesQuery = useQuery({
    queryKey: ["entityProfileModal", "caregiverRelationshipTypes"],
    queryFn: () =>
      api.get("./patient_caregiver_relationship_types", {}).then(normalizeRelationshipTypes),
    enabled: draft !== null,
  });

  const refreshRelationships = () => {
    queryClient.invalidateQueries({ queryKey: relationshipsQueryKey });
  };

  const saveRelationshipMutation = useMutation({
    mutationFn: (relationshipDraft: RelationshipDraft) => {
      const body = {
        caregiverId: relationshipDraft.caregiverId,
        endDate: LocalDate.parse(relationshipDraft.endDate),
        patientId: relationshipDraft.patientId,
        relationshipTypeId: relationshipDraft.relationshipTypeId,
        startDate: LocalDate.parse(relationshipDraft.startDate),
      };

      if (relationshipDraft.id === undefined) {
        return api.post("./patient_caregiver_relationship", { body });
      }

      return api.put("./patient_caregiver_relationship/:patientCaregiverRelationshipId", {
        path: { patientCaregiverRelationshipId: relationshipDraft.id },
        body,
      });
    },
    onSuccess: () => {
      setDraft(null);
      refreshRelationships();
      toast({
        description: "Relationship saved successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not save relationship.",
        status: "error",
      });
    },
  });
  const deleteRelationshipMutation = useMutation({
    mutationFn: (relationship: PatientCaregiverRelationship) =>
      api.delete("./patient_caregiver_relationship/:patientCaregiverRelationshipId", {
        path: { patientCaregiverRelationshipId: relationship.id },
      }),
    onSuccess: () => {
      refreshRelationships();
      toast({
        description: "Relationship deleted successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not delete relationship.",
        status: "error",
      });
    },
  });

  if (relationshipsQuery.isLoading) {
    return <EntityProfileLoadingState label="Loading patient-caregiver relationships" />;
  }

  if (relationshipsQuery.isError) {
    return (
      <EntityProfileErrorState
        label="Could not load patient-caregiver relationships."
        onRetry={() => relationshipsQuery.refetch()}
      />
    );
  }

  const rows: RelationshipWithDisplayData[] = relationshipsQuery.data?.relationships ?? [];
  const relationshipTypes = relationshipTypesQuery.data ?? [];
  const relatedEntityOptionsById = new Map(
    rows.map((row) => [
      Number(getRelatedEntityId(row, relatedEntity)),
      getLookupOption(row, relatedEntity),
    ])
  );
  const ownerEntityOptionsById = new Map(
    rows.map((row) => [
      Number(getRelatedEntityId(row, ownerEntity)),
      getLookupOption(row, ownerEntity),
    ])
  );
  const draftRelatedEntityId =
    draft === null
      ? 0
      : Number(relatedEntity === "caregiver" ? draft.caregiverId : draft.patientId);
  const draftRelatedEntityOption =
    draft === null || draftRelatedEntityId === 0
      ? null
      : relatedEntityOptionsById.get(draftRelatedEntityId) ?? {
          id: draftRelatedEntityId,
          displayId: draftRelatedEntityId,
          displayName: `${
            relatedEntity === "caregiver" ? "Caregiver" : "Patient"
          } ID ${draftRelatedEntityId}`,
        };
  const draftOwnerEntityId =
    draft === null ? 0 : Number(ownerEntity === "caregiver" ? draft.caregiverId : draft.patientId);
  const draftOwnerEntityOption =
    draft === null || draftOwnerEntityId === 0
      ? null
      : ownerEntityOptionsById.get(draftOwnerEntityId) ??
        props.owner.option ?? {
          id: draftOwnerEntityId,
          displayId: draftOwnerEntityId,
          displayName: `${
            ownerEntity === "caregiver" ? "Caregiver" : "Patient"
          } ID ${draftOwnerEntityId}`,
        };

  const saveDraft = () => {
    if (
      draft === null ||
      Number(draft.caregiverId) === 0 ||
      Number(draft.patientId) === 0 ||
      Number(draft.relationshipTypeId) === 0 ||
      draft.startDate === "" ||
      draft.endDate === ""
    ) {
      toast({
        description: "Patient, caregiver, dates, and relationship are required.",
        status: "error",
      });
      return;
    }

    saveRelationshipMutation.mutate(draft);
  };
  const openDraft = (relationshipDraft: RelationshipDraft) => {
    setDraft(relationshipDraft);
  };
  const openRelatedEntity = (relationship: RelationshipWithDisplayData) => {
    props.onOpenRelatedEntity(relationship);
  };

  return (
    <VStack alignItems="stretch" spacing={4}>
      <HStack justifyContent="flex-end">
        <EntityModalButton
          iconClassName="fa fa-plus"
          kind="blue"
          size="sm"
          onClick={() => openDraft(createDraft(props.owner))}
        >
          Add Relationship
        </EntityModalButton>
      </HStack>

      <Modal isOpen={draft !== null} size="3xl" onClose={() => setDraft(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {draft?.id === undefined ? "Add Relationship" : "Edit Relationship"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {draft !== null ? (
              <RelationshipForm
                draft={draft}
                isLoadingRelationshipTypes={relationshipTypesQuery.isLoading}
                isSaving={saveRelationshipMutation.isPending}
                owner={props.owner}
                ownerEntityOption={draftOwnerEntityOption}
                relatedEntityOption={draftRelatedEntityOption}
                relationshipTypes={relationshipTypes}
                relationshipTypesError={relationshipTypesQuery.isError}
                onCancel={() => setDraft(null)}
                onChange={setDraft}
                onSave={saveDraft}
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      <RelationshipTable
        isDeleting={deleteRelationshipMutation.isPending}
        relatedEntity={relatedEntity}
        rows={rows}
        onDelete={(relationship) => deleteRelationshipMutation.mutate(relationship)}
        onEdit={(relationship) => openDraft(mapRelationshipToDraft(relationship))}
        onOpenRelatedEntity={openRelatedEntity}
      />
    </VStack>
  );
}
